import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import authReducer from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import locationScopeReducer from './slices/locationScopeSlice';
import internalUserRolesReducer from './slices/internalUserRolesSlice';

// Redux Persist configuration for auth slice
const persistConfig = {
  key: 'auth',
  storage,
  whitelist: ['user', 'isAuthenticated'], // Only persist user and isAuthenticated
  // Don't persist: isLoading, error, token (handled by authService)
};

// Persist the resolved {id, name} lists so a page refresh doesn't lose them.
// status/error are transient and intentionally not whitelisted.
// COMP-1717: also persist `dispatchAreaIds` — it participates in the same
// tri-state semantics as states/districts (`null` = unrestricted, `[]` = zero
// DAs, `[id,...]` = subset). Without persistence, page reload would reset it
// to its `initialState` value of `null` and downstream code that reads the
// tri-state (isUnrestricted checks) would mis-classify scope-restricted
// users as unrestricted, leaking every DA/District in their assigned states.
const locationScopePersistConfig = {
  key: 'locationScope',
  storage,
  whitelist: ['states', 'districts', 'dispatchAreaIds'],
};

// Create persisted reducer
const persistedAuthReducer = persistReducer(persistConfig, authReducer);
const persistedLocationScopeReducer = persistReducer(locationScopePersistConfig, locationScopeReducer);

export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    ui: uiReducer,
    locationScope: persistedLocationScopeReducer,
    internalUserRoles: internalUserRolesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist action types
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // Ignore these paths in state
        ignoredPaths: ['auth.user.lastLogin'],
      },
    }),
  devTools: import.meta.env.DEV,
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks for better TypeScript integration
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;